import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, ArrowLeft, ArrowRight, Award, BookOpen, MessageCircle } from "lucide-react";
import CodeBlock from "../components/CodeBlock";
import { toast } from "sonner";
import { useAutoNarrate } from "../hooks/useAutoNarrate";
import SaveButton from "../components/SaveButton";

const mdComponents = {
  h1: ({ node, ...props }) => <h1 className="font-heading text-4xl md:text-5xl font-semibold mt-2 mb-4" {...props} />,
  h2: ({ node, ...props }) => <h2 className="font-heading text-2xl md:text-3xl font-semibold mt-7 mb-3" {...props} />,
  h3: ({ node, ...props }) => <h3 className="font-heading text-xl font-semibold mt-5 mb-2" {...props} />,
  p:  ({ node, ...props }) => <p  className="my-3 text-ink2 leading-relaxed" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-6 my-3 space-y-1.5 text-ink2 leading-relaxed" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-6 my-3 space-y-1.5 text-ink2 leading-relaxed" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => <strong className="text-ink font-semibold" {...props} />,
  em: ({ node, ...props }) => <em className="italic text-marigold" {...props} />,
  a: ({ node, ...props }) => <a className="text-marigold underline underline-offset-2 hover:text-marigold/80" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-saffron pl-4 my-4 italic text-ink2" {...props} />,
  code({ inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const code = String(children).replace(/\n$/, "");
    if (!inline && (match || code.includes("\n"))) {
      const lang = match ? match[1] : "swift";
      return <div className="my-4"><CodeBlock code={code} filename={`snippet.${lang}`} /></div>;
    }
    return <code className="px-1.5 py-0.5 rounded bg-paper border border-border font-mono text-[0.85em] text-ink" {...props}>{children}</code>;
  },
};

export default function LessonDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [done, setDone] = useState(false);
  const { register, Toggle: AutoplayToggle, AudioEl } = useAutoNarrate();

  useEffect(() => {
    api.get(`/lessons/${id}`).then(({ data }) => setLesson(data));
    if (user) api.get("/lessons/progress/me").then(({ data }) => {
      const me = data.find(p => p.lesson_id === id);
      if (me?.completed) setDone(true);
    });
  }, [id, user]);

  const markDone = async () => {
    if (!user) return toast("Log in to save progress", { description: "Sign up free to track your adventure." });
    try {
      await api.post("/lessons/progress", { lesson_id: id, completed: true });
      setDone(true);
      toast.success("Lesson complete ✨");
    } catch {
      toast.error("Could not save");
    }
  };

  if (!lesson) return <div className="container-narrow py-20 text-center text-ink2">Loading…</div>;

  // Split lesson content into paragraph-ish chunks so we can narrate them one
  // at a time as the reader scrolls.  Keep it lightweight — a real chunker
  // would use MDX; this is just the newline-split heuristic.
  const chunks = (lesson.content || "").split(/\n{2,}/).filter((c) => c.trim().length > 40);

  return (
    <div className="container-narrow py-12 md:py-16 max-w-3xl" data-testid="lesson-detail">
      <Link to="/lessons" className="inline-flex items-center gap-1.5 text-ink2 hover:text-ink mb-6" data-testid="lesson-back">
        <ArrowLeft className="w-4 h-4" /> Back to map
      </Link>

      <div className="mb-2 flex items-center gap-3 text-sm text-ink2">
        <span className="px-2.5 py-1 rounded-full bg-paper border border-border capitalize">{lesson.path} path</span>
        <span>{lesson.duration}</span>
        <span className="ml-auto"><AutoplayToggle testid="lesson-autoplay-toggle" /></span>
      </div>
      <AudioEl />

      <article>
        {chunks.map((chunk, i) => (
          <div key={i} ref={register(`lesson-${id}-c${i}`, chunk.replace(/[#*`>\-]/g, ""))} data-testid={`lesson-chunk-${i}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {chunk}
            </ReactMarkdown>
          </div>
        ))}
      </article>

      {/* Handpicked chapters from the curriculum for this lesson + Pradhan shortcut */}
      <LessonBonusReading lessonId={id} lessonTitle={lesson.title} />

      {lesson.checkpoint && (
        <div className="mt-10 card-soft p-6 border-l-4 border-saffron" data-testid="lesson-checkpoint">
          <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Checkpoint</div>
          <p className="text-ink leading-relaxed">{lesson.checkpoint}</p>
          <button onClick={markDone} disabled={done} className="mt-5 btn-primary text-sm py-2" data-testid="lesson-complete-button">
            {done ? <><Check className="w-4 h-4" /> Completed</> : "Mark as complete"}
          </button>
        </div>
      )}
    </div>
  );
}



// ─── Handpicked chapters from the curriculum + one-tap Pradhan shortcut for a lesson ───
function LessonBonusReading({ lessonId, lessonTitle }) {
  const [articles, setArticles] = useState(null);
  useEffect(() => {
    if (!lessonId) return;
    let cancelled = false;
    api.get(`/lessons/${lessonId}/bonus-reading?limit=4`)
      .then(({ data }) => {
        if (!cancelled) setArticles(Array.isArray(data.articles) ? data.articles : []);
      })
      .catch(() => { if (!cancelled) setArticles([]); });
    return () => { cancelled = true; };
  }, [lessonId]);

  const askPradhan = () => {
    // PradhanChat listens for this event — opens the panel and auto-sends the
    // question so the learner gets a personalised take + KB01 citations with
    // zero typing.
    const message = `Can you explain the lesson "${(lessonTitle || "this lesson").slice(0, 120)}" in your own words? Quote the curriculum where it helps.`;
    window.dispatchEvent(new CustomEvent("pradhan:open", { detail: { message } }));
  };

  if (articles === null) return null;

  return (
    <section className="mt-10 rounded-3xl border-2 border-royal/70 bg-parchment/70 shadow-[8px_8px_0_0_#2E1A47] overflow-hidden" data-testid="lesson-bonus-reading">
      <div className="px-6 pt-6 pb-4 border-b border-royal/15 flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-gold/25 text-royal flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-1 inline-flex items-center gap-2">
            <span>Handpicked</span>
            <span className="text-royal/40">·</span>
            <span className="text-royal/70">from the curriculum</span>
          </div>
          <h3 className="font-heading text-2xl font-semibold text-royal leading-tight">
            Read alongside this lesson.
          </h3>
        </div>
        <button
          type="button"
          onClick={askPradhan}
          className="btn-royal text-xs py-2 px-4 inline-flex items-center gap-1.5 flex-shrink-0"
          data-testid="lesson-ask-pradhan"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Ask Ms. Pradhan about this
        </button>
      </div>
      {articles.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-ink2 italic">
          No matching chapters from the curriculum yet — tap <span className="font-semibold text-royal">Ask Ms. Pradhan</span> instead.
        </div>
      ) : (
        <ul className="divide-y divide-royal/10">
          {articles.map((a) => (
            <li key={a.slug} className="flex items-start px-6 py-4 hover:bg-royal/[0.04] transition-colors group">
              <Link
                to={`/library/${a.slug}`}
                className="flex-1 flex items-start gap-3 min-w-0"
                data-testid={`lesson-bonus-${a.code}`}
              >
                <div className="pt-0.5 flex-shrink-0">
                  <div className="text-[10px] font-mono font-bold text-royal/70">
                    {a.module_code}·A{String(a.article_num || 0).padStart(2, "0")}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-base font-semibold text-royal group-hover:text-marigold transition-colors leading-snug">
                    {a.title}
                  </div>
                  {a.summary && (
                    <div className="text-xs text-ink2 mt-0.5 line-clamp-2 leading-relaxed">
                      {a.summary}
                    </div>
                  )}
                </div>
              </Link>
              <div className="pt-0.5 pl-3 flex items-center gap-2 flex-shrink-0">
                <SaveButton slug={a.slug} title={a.title} size="md" testId={`lesson-bonus-save-${a.code}`} />
                <ArrowRight className="w-4 h-4 text-royal/40 group-hover:text-marigold transition-colors" />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
